import { AccountType, IAccount } from "./account/IAccount";
import { IDocument } from "./Document";
import { BaseEntity, DefaultResponseWithData } from "./system";

export interface ISingleContactRespose
  extends DefaultResponseWithData<IContact> { }
export interface IAllContactsResponse
  extends DefaultResponseWithData<IContact[]> { }

export enum ContactType {
  Customer,
  Supplier,
  Staff,
  Driver,
  Admin,
  Mechanic,
  Exporter,
}

export interface IContact extends BaseEntity {
  contactType: ContactType;
  fullname: string;
  telephone1: string;
  telephone2: string;
  email: string;
  line1: string;
  line2: string;
  line3: string;
  line4: string;
  locality: string;
  town: string;
  county: string;
  postcode: string;
  notes: string;
  ebayUsername: string;

  deleted: string | null;

  accountId?: string;
  account: IAccount | null;

  // PUT /contact only. Leaving accountId out keeps the contact on its account; this is the only
  // way to take it off.
  removeFromAccount?: boolean;

  documents: IDocument[];

  //Frontend only
  hasDocuments: boolean;
}

// A lead hanging off a contact. Widget leads are handed to us on a contact the widget has already
// deduplicated on email + phone, so one seller quoting several vehicles lands on a single contact
// row - editing it from one lead rewrites the details on all of them.
export interface IContactLeadSummary {
  id: string;
  vehicleId: string | null;
  vrm: string | null;
  make: string | null;
  model: string | null;
  status: number;
  website: number;
  price: number;
  applyVAT: boolean;
  created: string;
  // Set when a contact merge moved this lead onto the contact.
  mergedFrom?: IMergedFrom | null;
}

// A lead or document a contact merge moved here: the "From merged: {name}" badge.
export interface IMergedFrom {
  mergeId: string;
  contactId: string;
  name: string | null;
  mergedAt: string;
}

// Persisted as ints on the API (Entities/Document.cs DocumentCopyReason).
export enum DocumentCopyReason {
  // Core recognised the seller as an existing contact on its own: someone's ID on a link no human checked.
  AutoLink,
  ChosenContact,
}

// A document copied onto a lead from a contact's documents: the "Copied from {name}" badge.
export interface ICopiedFrom {
  contactId: string | null;
  name: string | null;
  reason: DocumentCopyReason | null;
}

// GET /contact/{id}/merges - one contact merged into this one.
export interface IContactMergeSummary {
  mergeId: string;
  sourceContactId: string;
  sourceName: string | null;
  kind: "Manual" | "Tidy";
  mergedAt: string;
  mergedBy: string | null;
  undoneAt: string | null;
  undoneBy: string | null;
  leads: number;
  documents: number;
  messages: number;
  emails: number;
  orders: number;
}

// GET /contact/{id}/merge-preview - both sides of a merge before anything moves.
export interface IMergePreview {
  // The kept contact first.
  contacts: IMergePreviewContact[];
  // More than one means the accounts are merged first, into accountTargetId.
  accounts: IMergePreviewAccount[];
  accountTargetId: string | null;
  fieldsFilled: string[];
  refusal: string | null;
}

export interface IMergePreviewContact {
  id: string;
  fullname: string | null;
  email: string | null;
  telephone1: string | null;
  telephone2: string | null;
  postcode: string | null;
  accountId: string | null;
  accountNumber: number | null;
  created: string;
  isKept: boolean;
  leads: IContactLeadSummary[];
  documents: number;
  messages: number;
  emails: number;
  orders: number;
  refusal: string | null;
}

export interface IMergePreviewAccount {
  id: string;
  accountNumber: number;
  name: string | null;
  // Masked ("H Oakes, 20-75-01, ****0328"); null when the account has none.
  bankDetails: string | null;
  otherContacts: number;
  isTarget: boolean;
}

// GET /contact/merges/{mergeId}/unmerge-preview
export interface IUnmergePreview {
  mergeId: string;
  sourceContactId: string;
  sourceName: string | null;
  targetContactId: string;
  targetName: string | null;
  mergedAt: string;
  refusal: string | null;
  goingBack: IContactLeadSummary[];
  documents: number;
  messages: number;
  emails: number;
  orders: number;
  // Moved by staff since the merge - left where they are.
  staying: IContactLeadSummary[];
  // Reached the kept contact after the merge - the admin chooses for each.
  laterLeads: ILaterLead[];
  fieldsRestored: string[];
}

export interface ILaterLead extends IContactLeadSummary {
  suggestMove: boolean;
  reason: string;
}

// An existing contact that looks like the one about to be created - same email or same mobile
// (GET /contact/matches). The same shape as a contact search result, so it can be selected the same way.
// GET /contact/matches: a contact search result (typed as IContact, as /contact/search is) and
// what it matched on.
export interface IContactMatch extends IContact {
  matchedOnEmail: boolean;
  matchedOnMobile: boolean;
  leadCount: number;
}

export interface ISplitContactRequest {
  leadId: string;
  // Edited details to apply to the new copy. Omit to copy the contact as it stands.
  contact?: Partial<IContact> | null;
}

export interface ContactCreateEditRequest
  extends Omit<
    IContact,
    "created" | "updated" | "deleted" | "ebayUsername" | "accountId"
  > {
  createAccountQuery?: boolean;
  // The lead screen: someone Core recognises is returned instead of a new contact.
  reuseExistingQuery?: boolean;
  accountName: string | null;
  bankAccountName: string | null;
  bankAccountNumber: string | null;
  bankSortCode: string | null;
  accountType: AccountType;

  createAccount?: boolean;
}

